import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import dayjs from "dayjs";
import { db, ilike } from "../lib/api";
import { invalidate, useDebounced, useHotkey, useQuery } from "../lib/hooks";
import { AREA_LABEL, LEAD_AREAS, useRefData, type Area } from "../lib/refdata";
import { useSession } from "../lib/session";
import { Field, Select, Sheet, ErrorBox, useToast } from "./ui";
import { IconPlus } from "./icons";
import { ConflictNote, syncGoogleNow } from "./Appointments";
import { zoneLabel, zoneLocal, zoneToUtc, type Zone } from "../lib/zones";
import { MeetingWhen, meetingIso } from "./MeetingWhen";
import { ReferralLine } from "./ReferralLine";

type Preset = { area?: Area; person_id?: string; person_name?: string; activity_type?: string; follow_up_id?: string; edit?: any; channel?: string; what_happened?: string };
const LogCtx = createContext<(p?: Preset) => void>(() => undefined);
export const useLog = () => useContext(LogCtx);

export function LogProvider({ children, workspace }: { children: ReactNode; workspace: "home" | "leads" | "team" | "clients" | "settings" }) {
  const [preset, setPreset] = useState<Preset | null>(null);
  const { can } = useSession();
  const allowed = can("activities", "edit");
  const open = (p: Preset = {}) => allowed && setPreset(p);
  const dflt = (): Preset => (workspace === "team" ? { area: "team" } : workspace === "clients" ? { area: "clients" } : {});
  useHotkey("l", () => open(dflt()));
  return (
    <LogCtx.Provider value={open}>
      {children}
      {allowed && workspace !== "settings" && (
        <button className="fab" onClick={() => open(dflt())} aria-label="Log an activity (L)">
          <IconPlus /> Log <kbd>L</kbd>
        </button>
      )}
      {preset && <LogSheet preset={preset} workspace={workspace} onClose={() => setPreset(null)} />}
    </LogCtx.Provider>
  );
}

function LogSheet({ preset, workspace, onClose }: { preset: Preset; workspace: string; onClose: () => void }) {
  const ref = useRefData();
  const toast = useToast();
  const { can } = useSession();
  const ed = preset.edit;
  const [area, setArea] = useState<Area>(preset.area ?? (workspace === "team" ? "team" : workspace === "clients" ? "clients" : "warm"));
  const [person, setPerson] = useState<{ id: string; name: string } | null>(
    preset.person_id ? { id: preset.person_id, name: preset.person_name ?? "" } : null);
  const [f, setF] = useState(() => {
    const blank = {
      occurred_on: dayjs().format("YYYY-MM-DD"),
      channel: preset.channel ?? "", activity_type: preset.activity_type ?? "", what_happened: preset.what_happened ?? "", next_step: "", follow_up_on: "",
      topic: "", commitment: "", commitment_due: "", commitment_status: "", spark: "", contacted_by: "",
    };
    if (!ed) return blank;
    const out: typeof blank = { ...blank };
    for (const k of Object.keys(blank) as (keyof typeof blank)[]) out[k] = ed[k] ?? "";
    return out;
  });
  // A follow-up date is required, unless you say why none is needed.
  const [noFollow, setNoFollow] = useState<string>(ed && !ed.follow_up_on ? "Edited" : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));

  const types = useMemo(() => ref.data?.typesFor(area) ?? [], [ref.data, area]);
  const chosen = ref.data?.types.find((t) => t.name === f.activity_type);
  useEffect(() => {
    if (f.activity_type && types.length && !types.some((t) => t.name === f.activity_type)) setF((x) => ({ ...x, activity_type: "" }));
  }, [area, types, f.activity_type]);

  const isNote = !!chosen?.is_note;
  // "… booked" (review, appointment, coffee): book the meeting on the same screen
  const booking = !ed && /booked$/i.test(f.activity_type);
  const defaultLink: string = ref.data?.setting("notifications")?.default_meeting_url ?? "";
  const [meet, setMeet] = useState<{ at: string; minutes: string; url: string; zone: Zone }>({ at: "", minutes: "60", url: "", zone: "ON" });
  async function save(another = false) {
    if (!person || !f.activity_type) return;
    setBusy(true);
    setError(null);
    try {
      if (booking && !meet.at) throw new Error("When is it? Choose the meeting date and time, and it goes into Meetings and your Google Calendar.");
      if (!isNote && !booking && !f.follow_up_on && !noFollow) throw new Error("Choose when to follow up, or tick “No follow-up needed” and say why. Every conversation needs a next step.");
      const row: Record<string, unknown> = {
        occurred_on: f.occurred_on, area, person_id: person.id, activity_type: f.activity_type,
        channel: f.channel || null, what_happened: f.what_happened || null,
        next_step: f.next_step || (booking ? `Meeting ${dayjs(meet.at).format("ddd MMM D, h:mm A")}${meet.zone !== "ON" ? ` ${zoneLabel(meet.zone)}` : ""}` : null) || (!f.follow_up_on && noFollow && noFollow !== "Edited" ? `No follow-up: ${noFollow}` : null),
        follow_up_on: f.follow_up_on || null,
      };
      if (area === "team") Object.assign(row, {
        topic: f.topic || null, commitment: f.commitment || null, commitment_due: f.commitment_due || null,
        commitment_status: f.commitment ? f.commitment_status || "Pending" : null, spark: f.spark || null,
        contacted_by: f.contacted_by || null,
      });
      if (ed) await db.update("activities", { id: `eq.${ed.id}` }, row);
      else await db.insert("activities", row);
      if (preset.follow_up_id) await db.update("activities", { id: `eq.${preset.follow_up_id}` }, { follow_up_done: true });
      if (booking) {
        const starts = dayjs(zoneToUtc(meet.at, meet.zone)), ends = starts.add(Number(meet.minutes) || 60, "minute");
        if (f.activity_type === "Review booked") {
          // the log moved their review to Booked; give it the time (that makes the meeting)
          const [r] = await db.select<{ id: string }>("client_reviews", { person_id: `eq.${person.id}`, status: "eq.Booked", select: "id", order: "created_at.desc", limit: 1 });
          let rid = r?.id;
          if (rid) await db.update("client_reviews", { id: `eq.${rid}` }, { scheduled_at: starts.toISOString() });
          else rid = (await db.insert<{ id: string }>("client_reviews", { person_id: person.id, review_type: "Annual Review", status: "Booked", scheduled_at: starts.toISOString(), due_on: starts.format("YYYY-MM-DD") }))[0]?.id;
          if (rid) await db.update("appointments", { external_id: `eq.review:${rid}` }, { ends_at: ends.toISOString(), zone_code: meet.zone, ...(meet.url ? { meeting_url: meet.url } : {}) }).catch(() => undefined);
        } else {
          await db.insert("appointments", { person_id: person.id, title: f.activity_type.replace(/ booked$/i, ""), starts_at: starts.toISOString(), ends_at: ends.toISOString(),
            meeting_url: meet.url || null, source: "crm", remind: true, zone_code: meet.zone });
        }
        invalidate("appts"); syncGoogleNow();
        db.rpc("meeting_emails_now").catch(() => undefined);
      }
      invalidate("today");
      invalidate(`person:${person.id}`);
      invalidate("people");
      invalidate("team"); invalidate("cc"); invalidate("actlog"); invalidate("clients"); invalidate("reviews");
      toast(ed ? "Entry updated" : `Logged ${f.activity_type.toLowerCase()} with ${person.name}`);
      if (another) {
        setPerson(null);
        setF((x) => ({ ...x, what_happened: "", next_step: "", follow_up_on: "", commitment: "", commitment_due: "" }));
        setNoFollow("");
      } else onClose();
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(false);
    }
  }

  const fu = (days: number) => set("follow_up_on")(dayjs().add(days, "day").format("YYYY-MM-DD"));
  // Only the lists that belong to where you are: Leads → lead lists, Team → team, Clients → clients; elsewhere, all.
  const scoped: Area[] = workspace === "team" ? ["team"] : workspace === "clients" ? ["clients"] : workspace === "leads" ? [...LEAD_AREAS]
    : [...LEAD_AREAS, ...(can("team") ? ["team" as Area] : []), ...(can("clients") ? ["clients" as Area] : [])];
  const areas: Area[] = preset.area && !scoped.includes(preset.area) ? [preset.area] : scoped;

  return (
    <Sheet
      title={ed ? "Edit log entry" : "Log an activity"}
      onClose={onClose}
      footer={<>
        {!ed && <button className="btn ghost" onClick={() => save(true)} disabled={busy || !person || !f.activity_type}>Save and log another</button>}
        <button className="btn primary" onClick={() => save(false)} disabled={busy || !person || !f.activity_type}>{busy ? "Saving…" : "Save"}</button>
      </>}
    >
      <ErrorBox error={error} />
      {!preset.person_id && (
        <Field label="Area">
          <div className="seg">
            {areas.map((a) => (
              <button key={a} type="button" aria-pressed={area === a} onClick={() => { setArea(a); setPerson(null); }}>{AREA_LABEL[a]}</button>
            ))}
          </div>
        </Field>
      )}
      <PersonPicker area={area} value={person} onChange={setPerson} locked={!!preset.person_id} />
      {!f.activity_type && types.length > 0 && (
        <div className="toolbar" style={{ margin: 0 }}>
          <span className="small muted">Quick:</span>
          {types.filter((t) => /call|message|met in person|talked|check-in|note/i.test(t.name)).slice(0, 6).map((t) => (
            <button key={t.name} type="button" className="btn small" onClick={() => set("activity_type")(t.name)}>{t.name}</button>
          ))}
        </div>
      )}
      {person && /referral/i.test(f.activity_type) && <div className="note-strip small" style={{ margin: "0 0 10px" }}><ReferralLine personId={person.id} long /></div>}
      <div className="form-grid">
        <Field label="Activity">
          <Select value={f.activity_type} onChange={set("activity_type")} options={types.map((t) => t.name)} placeholder="Choose what you did" />
        </Field>
        <Field label="Channel">
          <Select value={f.channel} onChange={set("channel")} options={ref.data?.list("Channels") ?? []} />
        </Field>
        <Field label="Date">
          <input type="date" className="input" value={f.occurred_on} onChange={(e) => set("occurred_on")(e.target.value)} />
        </Field>
      </div>
      {booking && (
        <div className="form-grid">
          <Field label="Meeting date, time and time zone" wide><MeetingWhen value={meet.at} zone={meet.zone} onChange={(at, zone) => setMeet({ ...meet, at, zone, url: meet.url || defaultLink })} /></Field>
          <Field label="Length"><Select value={meet.minutes} onChange={(v) => setMeet({ ...meet, minutes: v || "60" })}
            options={["15", "30", "45", "60", "90", "120"].map((m) => ({ value: m, label: Number(m) < 60 ? `${m} min` : `${Number(m) / 60} h` }))} /></Field>
          <Field label="Zoom / meeting link" wide><input className="input" value={meet.url} placeholder="https://zoom.us/j/…" onChange={(e) => setMeet({ ...meet, url: e.target.value })} /></Field>
          <div className="wide"><ConflictNote start={meet.at ? zoneLocal(meetingIso(meet.at, meet.zone), "ON") : meet.at} minutes={meet.minutes} /></div>
          <p className="small muted wide" style={{ margin: 0 }}>One step: this logs it, puts the meeting in Meetings and your Google Calendar, and emails them reminders.</p>
        </div>
      )}
      {chosen && (
        <div className="small muted">
          {chosen.give_ask === "give" ? "A give: it builds the relationship. " : chosen.give_ask === "ask" ? "An ask: make sure you've given first. " : ""}
          {chosen.counts_as ? "Counts toward today's targets." : "Relationship touch only; not in the daily counts."}
        </div>
      )}
      <Field label="What happened">
        <textarea className="textarea" value={f.what_happened} onChange={(e) => set("what_happened")(e.target.value)} placeholder="One line: what was said or agreed" />
      </Field>
      {area === "team" && (
        <div className="form-grid">
          <Field label="Topic"><Select value={f.topic} onChange={set("topic")} options={ref.data?.list("TeamTopics") ?? []} /></Field>
          <Field label="Contacted by"><Select value={f.contacted_by} onChange={set("contacted_by")} options={ref.data?.list("ContactedBy") ?? []} /></Field>
          <Field label="Spark (how ready they are)"><Select value={f.spark} onChange={set("spark")} options={ref.data?.list("Spark") ?? []} /></Field>
          <Field label="Their commitment" wide>
            <input className="input" value={f.commitment} onChange={(e) => set("commitment")(e.target.value)} placeholder="What they said they'll do" />
          </Field>
          {f.commitment && <>
            <Field label="Their deadline"><input type="date" className="input" value={f.commitment_due} onChange={(e) => set("commitment_due")(e.target.value)} /></Field>
            <Field label="Status"><Select value={f.commitment_status || "Pending"} onChange={set("commitment_status")} options={ref.data?.list("CommitmentStatus") ?? []} /></Field>
          </>}
        </div>
      )}
      <div className="form-grid">
        <Field label="Next step / next time ask about">
          <input className="input" value={f.next_step} onChange={(e) => set("next_step")(e.target.value)} />
        </Field>
        <Field label="Follow up on">
          <input type="date" className="input" value={f.follow_up_on} onChange={(e) => set("follow_up_on")(e.target.value)} />
        </Field>
      </div>
      <div className="toolbar" style={{ margin: 0 }}>
        <span className="small muted">Follow up:</span>
        <button type="button" className="btn small" onClick={() => fu(1)}>Tomorrow</button>
        <button type="button" className="btn small" onClick={() => fu(3)}>In 3 days</button>
        <button type="button" className="btn small" onClick={() => fu(7)}>Next week</button>
        <button type="button" className="btn small" onClick={() => fu(14)}>In 2 weeks</button>
        {f.follow_up_on && <button type="button" className="btn small ghost" onClick={() => set("follow_up_on")("")}>Clear</button>}
      </div>
      {!isNote && !booking && !f.follow_up_on && (
        <div className="row-inline" style={{ margin: 0 }}>
          <label className="switch-row" style={{ marginTop: 0 }}>
            <input type="checkbox" checked={!!noFollow} onChange={(e) => setNoFollow(e.target.checked ? "Nothing pending" : "")} /> No follow-up needed
          </label>
          {noFollow && <Select value={noFollow} onChange={(v) => setNoFollow(v || "Nothing pending")} style={{ maxWidth: 260 }}
            options={["Nothing pending", "They'll reach out", "Not interested", "Moved to pipeline", "Wrong number / no longer relevant", ...(noFollow === "Edited" ? ["Edited"] : [])]} />}
        </div>
      )}
    </Sheet>
  );
}

function PersonPicker({ area, value, onChange, locked }: { area: Area; value: { id: string; name: string } | null; onChange: (v: { id: string; name: string } | null) => void; locked?: boolean }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 200);
  const safe = dq.replace(/[*,()]/g, " ");
  const results = useQuery(dq.length >= 2 ? `pick:${area}:${dq}` : null, async () => {
    if (area === "clients") {
      const r = await db.select<{ id: string; full_name: string; phone: string | null; known_as: string | null }>("people",
        { select: "id,full_name,phone,known_as", is_client: "is.true", deleted_at: "is.null", or: `(full_name.ilike.*${safe}*,known_as.ilike.*${safe}*)`, order: "full_name", limit: 8 });
      return r.map((x) => ({ id: x.id, name: x.full_name, sub: [x.known_as, x.phone].filter(Boolean).join(" · ") }));
    }
    if (area === "team") {
      const r = await db.select<{ person_id: string; full_name: string; agent_code: string; status: string }>("team_overview",
        { select: "person_id,full_name,agent_code,status", full_name: ilike(dq), order: "status,full_name", limit: 8 });
      return r.map((x) => ({ id: x.person_id, name: x.full_name, sub: `${x.agent_code ?? ""} · ${x.status}` }));
    }
    const r = await db.select<{ id: string; full_name: string; phone: string | null; known_as: string | null }>("people",
      { select: "id,full_name,phone,known_as,person_areas!inner(area)", "person_areas.area": `eq.${area}`, deleted_at: "is.null",
        or: `(full_name.ilike.*${safe}*,known_as.ilike.*${safe}*)`, order: "full_name", limit: 8 });
    return r.map((x) => ({ id: x.id, name: x.full_name, sub: [x.known_as, x.phone].filter(Boolean).join(" · ") }));
  });

  if (value) {
    return (
      <Field label="Person">
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <strong>{value.name}</strong>
          {!locked && <button type="button" className="btn small ghost" onClick={() => onChange(null)}>Change</button>}
        </div>
      </Field>
    );
  }
  return (
    <Field label={`Person in ${AREA_LABEL[area]}`}>
      <input className="input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Start typing a name" />
      {dq.length >= 2 && (
        <div className="panel" style={{ marginTop: 4, maxHeight: 220, overflowY: "auto" }}>
          {(results.data ?? []).map((r) => (
            <button key={r.id} type="button" className="btn ghost" style={{ width: "100%", justifyContent: "space-between", borderRadius: 0 }}
              onClick={() => onChange({ id: r.id, name: r.name })}>
              <span>{r.name}</span><span className="small muted">{r.sub}</span>
            </button>
          ))}
          {results.data?.length === 0 && <div className="empty small">No one called “{dq}” in {AREA_LABEL[area]}.</div>}
        </div>
      )}
    </Field>
  );
}
